import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ANALYTICS_API = `${BACKEND_URL}/api/analytics`;

/**
 * DatasetSelector
 * Shows dataset GROUPS (not raw files) so the user selects at the group level.
 * Each group is either:
 *   - Independent: single dataset, standalone analysis
 *   - Linked: multiple datasets joined on a common ID column
 *
 * This eliminates the resolver ambiguity where mixed-group sessions caused
 * wrong strategy detection at query time.
 */
const DatasetSelector = ({
  mrsId,
  userId,
  onSessionStart,
  onDatasetsChanged,
  compact = false,
  initialSelected = null,
}) => {
  const [groups, setGroups]           = useState([]);
  const [graphReady, setGraphReady]   = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [starting, setStarting]       = useState(false);
  const [error, setError]             = useState(null);
  const [generating, setGenerating]   = useState(false);
  const [generateMsg, setGenerateMsg] = useState(null);

  // Upload state
  const [uploadFile, setUploadFile]       = useState(null);
  const [uploading, setUploading]         = useState(false);
  const [uploadMsg, setUploadMsg]         = useState(null);
  const [uploadError, setUploadError]     = useState(null);
  const [showUpload, setShowUpload]       = useState(false);
  const fileInputRef = useRef(null);

  const loadGroups = () => {
    if (!mrsId) return;
    setLoading(true);
    setError(null);
    axios
      .get(`${ANALYTICS_API}/mrs/${mrsId}/groups?user_id=${userId}`)
      .then((res) => {
        const list = res.data.groups || [];
        setGroups(list);
        setGraphReady(res.data.graph_ready || false);
        // Auto-select all groups by default
        if (initialSelected === null) {
          setSelectedGroupIds(list.map((g) => g.group_id || g.label));
        } else {
          setSelectedGroupIds(initialSelected);
        }
        if (list.length === 0) setShowUpload(true);
      })
      .catch(() => setError('Failed to load datasets. Please try again.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadGroups(); }, [mrsId, userId]); // eslint-disable-line

  const toggleGroup = (groupKey) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupKey)
        ? prev.filter((id) => id !== groupKey)
        : [...prev, groupKey]
    );
  };

  const selectAll   = () => setSelectedGroupIds(groups.map((g) => g.group_id || g.label));
  const deselectAll = () => setSelectedGroupIds([]);

  /* ── Analytics file upload ─────────────────────────────────────────── */
  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const name = f.name.toLowerCase();
    if (!name.endsWith('.xlsx') && !name.endsWith('.xls') && !name.endsWith('.csv')) {
      setUploadError('Only .xlsx, .xls, and .csv files are supported.');
      return;
    }
    setUploadFile(f);
    setUploadError(null);
    setUploadMsg(null);
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadError(null);
    setUploadMsg(null);
    const formData = new FormData();
    formData.append('file', uploadFile);
    try {
      const res = await axios.post(
        `${ANALYTICS_API}/datasets/upload?mrs_id=${mrsId}&user_id=${userId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const d = res.data;
      setUploadMsg(
        d.was_deduplicated
          ? `"${d.file_name}" was already uploaded — using cached version.`
          : `"${d.file_name}" uploaded successfully (${d.row_count?.toLocaleString()} rows, ${d.column_count} columns).`
      );
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadGroups();
      if (onDatasetsChanged) onDatasetsChanged();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setUploadError(
        typeof detail === 'object' ? detail.message : (detail || 'Upload failed.')
      );
    } finally {
      setUploading(false);
    }
  };

  /* ── Generate AI questions ─────────────────────────────────────────── */
  const handleGenerateQuestions = async () => {
    setGenerating(true);
    setGenerateMsg(null);
    setError(null);
    try {
      const res = await axios.post(
        `${ANALYTICS_API}/mrs/${mrsId}/generate-questions-all?user_id=${userId}`
      );
      const { queued } = res.data;
      if (queued.length === 0) {
        setGenerateMsg('All datasets already have AI-generated questions.');
      } else {
        setGenerateMsg(
          `Generating questions for ${queued.length} dataset(s) in the background (~30s per dataset).`
        );
      }
    } catch {
      setError('Failed to trigger question generation. Check that the OpenAI key is configured.');
    } finally {
      setGenerating(false);
    }
  };

  /* ── Start session ─────────────────────────────────────────────────── */
  const handleStartAnalysis = async () => {
    const chosenGroups = groups.filter((g) =>
      selectedGroupIds.includes(g.group_id || g.label)
    );
    if (chosenGroups.length === 0) return;

    // Collect all dataset_ids from chosen groups
    const allDatasetIds = chosenGroups.flatMap((g) => g.dataset_ids || []);

    setStarting(true);
    setError(null);
    try {
      const res = await axios.post(
        `${ANALYTICS_API}/sessions/start?user_id=${userId}`,
        {
          mrs_id: mrsId,
          dataset_ids: allDatasetIds,
          selected_groups: chosenGroups,
        }
      );
      onSessionStart(res.data);
    } catch (err) {
      setError(
        err?.response?.data?.detail?.message ||
        'Failed to start session. Please try again.'
      );
    } finally {
      setStarting(false);
    }
  };

  /* ── Upload panel ──────────────────────────────────────────────────── */
  const UploadPanel = () => (
    <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-gray-700">Upload dataset for analytics</p>
        {groups.length > 0 && (
          <button
            onClick={() => setShowUpload(false)}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            ✕ close
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Excel or CSV only. The file will be processed and grouped automatically.
      </p>
      <div className="flex gap-2 items-center">
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileChange}
          className="flex-1 text-sm text-gray-600
                     file:mr-3 file:py-1.5 file:px-3 file:rounded-lg
                     file:border-0 file:text-sm file:font-medium
                     file:bg-blue-50 file:text-blue-700
                     hover:file:bg-blue-100 cursor-pointer"
        />
        <Button onClick={handleUpload} disabled={!uploadFile || uploading} size="sm" className="shrink-0">
          {uploading ? 'Uploading…' : 'Upload'}
        </Button>
      </div>
      {uploadError && (
        <Alert variant="destructive" className="mt-3">
          <AlertDescription>{uploadError}</AlertDescription>
        </Alert>
      )}
      {uploadMsg && (
        <Alert className="mt-3 border-green-200 bg-green-50 text-green-800">
          <AlertDescription>{uploadMsg}</AlertDescription>
        </Alert>
      )}
    </div>
  );

  /* ── Loading ───────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="flex items-center justify-center p-6 text-gray-400 text-sm">
        Loading datasets…
      </div>
    );
  }

  /* ── Empty state ───────────────────────────────────────────────────── */
  if (groups.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {!compact && (
          <Alert className="border-amber-200 bg-amber-50 text-amber-800">
            <AlertDescription>
              No analytics datasets uploaded for this MRS yet. Upload an Excel or CSV file below to get started.
            </AlertDescription>
          </Alert>
        )}
        <UploadPanel />
      </div>
    );
  }

  /* ── Group cards ───────────────────────────────────────────────────── */
  return (
    <div className="flex flex-col gap-3">

      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        {!compact && (
          <p className="text-sm text-gray-600 flex-1">
            Select which dataset group(s) to analyse.
          </p>
        )}
        <div className="flex items-center gap-3 ml-auto">
          {groups.length > 1 && (
            <span className="text-xs text-gray-400">
              <button onClick={selectAll} className="text-blue-600 hover:underline">All</button>
              {' / '}
              <button onClick={deselectAll} className="text-blue-600 hover:underline">None</button>
            </span>
          )}
          <button
            onClick={() => { setShowUpload((v) => !v); setUploadError(null); setUploadMsg(null); }}
            className="text-xs text-blue-600 hover:text-blue-800 underline shrink-0"
          >
            {showUpload ? 'Hide upload' : '+ Upload dataset'}
          </button>
        </div>
      </div>

      {showUpload && <UploadPanel />}

      {/* Group cards */}
      <div className="grid gap-2">
        {groups.map((grp) => {
          const groupKey = grp.group_id || grp.label;
          const isSelected = selectedGroupIds.includes(groupKey);
          return (
            <Card
              key={groupKey}
              className={`cursor-pointer border-2 transition-colors ${
                isSelected
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => toggleGroup(groupKey)}
            >
              <CardContent className={`flex items-start gap-3 ${compact ? 'py-3' : 'py-4'}`}>
                {/* Selection indicator */}
                <div className={`mt-0.5 w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center ${
                  isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                }`}>
                  {isSelected && (
                    <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 12 12">
                      <path d="M10 3L5 8.5 2 5.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  {/* Group label */}
                  <p className="font-semibold text-gray-900 text-sm">{grp.label}</p>

                  {/* Files in this group */}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(grp.member_files || []).map((fname) => (
                      <span
                        key={fname}
                        className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full"
                      >
                        {fname}
                      </span>
                    ))}
                  </div>

                  {/* Stats + type badge */}
                  <div className="flex gap-2 mt-1.5 text-xs text-gray-500 flex-wrap items-center">
                    <span>{grp.row_count?.toLocaleString()} rows</span>
                    <span>·</span>
                    {grp.is_joinable ? (
                      <Badge className="text-xs py-0 bg-green-100 text-green-700 border-green-200">
                        linked · joined on {grp.join_key || 'ID'}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs py-0 text-gray-500">
                        independent
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Info when multiple groups selected */}
      {selectedGroupIds.length > 1 && (
        <p className="text-xs text-blue-600 bg-blue-50 rounded-lg px-3 py-2">
          Both groups will be analysed independently and results shown side by side.
        </p>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        <Button
          onClick={handleStartAnalysis}
          disabled={selectedGroupIds.length === 0 || starting}
          className="flex-1"
          size={compact ? 'sm' : 'default'}
        >
          {starting
            ? 'Starting…'
            : compact
              ? `Apply (${selectedGroupIds.length} group${selectedGroupIds.length !== 1 ? 's' : ''})`
              : `Start Analysis (${selectedGroupIds.length} group${selectedGroupIds.length !== 1 ? 's' : ''})`
          }
        </Button>
        {!compact && (
          <Button
            variant="outline"
            onClick={handleGenerateQuestions}
            disabled={generating}
            title="Generate 50 AI questions for all datasets"
          >
            {generating ? 'Generating…' : 'Generate Questions'}
          </Button>
        )}
      </div>

      {generateMsg && (
        <Alert>
          <AlertDescription>{generateMsg}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

DatasetSelector.propTypes = {
  mrsId:             PropTypes.string.isRequired,
  userId:            PropTypes.string.isRequired,
  onSessionStart:    PropTypes.func.isRequired,
  onDatasetsChanged: PropTypes.func,
  compact:           PropTypes.bool,
  initialSelected:   PropTypes.arrayOf(PropTypes.string),
};

export default DatasetSelector;
